import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './LandingPage.css';
import { isLandingFeatureKey, isLandingPartnerLogoKey, featureSlideKeys } from '../services/landingPageService';
import { loadPublicLandingSections, peekCachedPublicLandingSections } from '../utils/landingPublicContent';
import LandingNav from '../components/landing/LandingNav';
import PlatformCompanyInfo from '../components/landing/PlatformCompanyInfo';
import LandingFeatureMedia from '../components/landing/LandingFeatureMedia';
import BrandLogo from '../components/BrandLogo';
import type { LandingPageSection } from '../types/landingPage';
import type { LandingFeatureSlide } from '../components/landing/LandingFeatureMedia';

const CAROUSEL_INTERVAL_MS = 5500;

interface LandingPageProps {
  onLoginClick: () => void;
  onSignUpClick: () => void;
}

interface CarouselSlide {
  id: string;
  imageUrl: string | null;
  /** 세로형 화면(모바일)용 이미지 */
  mobileImageUrl?: string | null;
  alt: string;
  tone: number;
  /** 헤드라인·카피가 이미지에 포함된 슬라이드 (HTML 제목/본문은 시각적으로 숨김) */
  textBaked?: boolean;
}

/** 기본 히어로 이미지 (public/landing-hero). CMS에 히어로 슬라이드 이미지가 없을 때 사용 */
const BUILTIN_HERO_SLIDES: CarouselSlide[] = [
  {
    id: 'builtin-hero-1',
    imageUrl: '/landing-hero/hero-1.webp',
    mobileImageUrl: '/landing-hero/hero-1-mobile.webp',
    alt: '교육과정 설계 · 운영 — 우리 반 교육과정 한 화면에서 설계하세요.',
    tone: 0,
    textBaked: true,
  },
  {
    id: 'builtin-hero-2',
    imageUrl: '/landing-hero/hero-2.webp',
    mobileImageUrl: '/landing-hero/hero-2-mobile.webp',
    alt: '업무관리 · 일정관리 · 예산관리 — 바쁜 학교 업무와 일정, 꼼꼼한 예산관리도 완벽하게!',
    tone: 1,
    textBaked: true,
  },
  {
    id: 'builtin-hero-3',
    imageUrl: '/landing-hero/hero-3.webp',
    mobileImageUrl: '/landing-hero/hero-3-mobile.webp',
    alt: '수업운영 툴 — 수업시간 관리부터 프레젠테이션, 전자칠판 기능까지 수업을 돕는 다양한 툴을 제공합니다.',
    tone: 2,
    textBaked: true,
  },
  {
    id: 'builtin-hero-4',
    imageUrl: '/landing-hero/hero-4.webp',
    mobileImageUrl: '/landing-hero/hero-4-mobile.webp',
    alt: '학급운영 — 다양한 유형의 과제 생성과 관리, 게이미피케이션을 적용한 학급관리 포인트, 아이템 시스템으로 학급에 재미를 추가해보세요!',
    tone: 0,
    textBaked: true,
  },
  {
    id: 'builtin-hero-5',
    imageUrl: '/landing-hero/hero-5.webp',
    mobileImageUrl: '/landing-hero/hero-5-mobile.webp',
    alt: '평가 · 기록 관리 — 교육과정과 연계한 평가 등록과 학생 수업, 행동 기록을 하나로 관리하세요!',
    tone: 1,
    textBaked: true,
  },
];

const HERO_MOBILE_MEDIA = '(max-width: 640px), (max-aspect-ratio: 3/4)';

function isSignUpCta(section: LandingPageSection): boolean {
  const label = (section.cta_label ?? '').trim();
  const link = (section.cta_link ?? '').trim().toLowerCase();
  if (section.section_key === 'cta') return true;
  if (label.includes('회원가입') || label.includes('가입')) return true;
  if (link.includes('signup') || link.includes('sign-up') || link.includes('register')) return true;
  return false;
}

function sectionImageUrl(section: LandingPageSection): string | null {
  if (!section.image_url) return null;
  return `${section.image_url}?v=${section.image_version}`;
}

function buildFeatureMediaSlides(
  sections: LandingPageSection[],
  featureKey: string,
): LandingFeatureSlide[] {
  const fromSlides = featureSlideKeys(featureKey)
    .map((key) => sections.find((s) => s.section_key === key))
    .filter((s): s is LandingPageSection => Boolean(s?.image_url))
    .map((s) => ({ id: s.id, url: sectionImageUrl(s)! }));

  if (fromSlides.length > 0) return fromSlides;

  // 마이그레이션 전 단일 이미지 폴백
  const parent = sections.find((s) => s.section_key === featureKey);
  const parentUrl = parent ? sectionImageUrl(parent) : null;
  if (parent && parentUrl) return [{ id: parent.id, url: parentUrl }];
  return [];
}

function buildCarouselSlides(sections: LandingPageSection[]): CarouselSlide[] {
  const slides = [1, 2, 3].map((n, i) => {
    const section = sections.find((s) => s.section_key === `hero_slide${n}`);
    return {
      id: section?.id ?? `hero-slide-${n}`,
      imageUrl: section ? sectionImageUrl(section) : null,
      alt: section?.title || `히어로 이미지 ${n}`,
      tone: i % 3,
    };
  });

  const withImages = slides.filter((s) => s.imageUrl);
  if (withImages.length > 0) {
    return withImages;
  }

  return BUILTIN_HERO_SLIDES;
}

function useScrollReveal(enabled: boolean) {
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;

    const targets = root.querySelectorAll<HTMLElement>('[data-landing-reveal]');
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled]);

  return rootRef;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick, onSignUpClick }) => {
  const [sections, setSections] = useState<LandingPageSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadSections = async () => {
      try {
        const cached = peekCachedPublicLandingSections();
        if (cached?.length) {
          setSections(cached);
          setLoading(false);
        }

        const fresh = await loadPublicLandingSections();
        if (cancelled) return;
        setSections(fresh);
        setLoading(false);
        setError(null);
      } catch (err) {
        console.error('랜딩 페이지 콘텐츠 로드 실패:', err);
        if (cancelled) return;
        if (!peekCachedPublicLandingSections()?.length) {
          setError('페이지를 불러올 수 없습니다.');
        }
        setLoading(false);
      }
    };
    void loadSections();
    return () => {
      cancelled = true;
    };
  }, []);

  const orderedTopLevel = useMemo(() => {
    const top = sections
      .filter(
        (s) =>
          s.section_key === 'hero' ||
          s.section_key === 'cta' ||
          s.section_key === 'partners' ||
          isLandingFeatureKey(s.section_key),
      )
      .sort((a, b) => a.section_order - b.section_order);
    if (!top.some((s) => s.section_key === 'partners')) {
      return [
        ...top,
        {
          id: 'partners-fallback',
          section_key: 'partners',
          section_order: Number.MAX_SAFE_INTEGER,
          title: '협업기관',
          subtitle: '함께하는 기관·학교',
          body: null,
          cta_label: null,
          cta_link: null,
          image_path: null,
          image_url: null,
          image_version: 1,
          is_active: true,
          created_at: '',
          updated_at: '',
        } satisfies LandingPageSection,
      ];
    }
    return top;
  }, [sections]);
  const orderedBlocks = useMemo(() => {
    type Block =
      | { type: 'hero'; section: LandingPageSection }
      | { type: 'cta'; section: LandingPageSection }
      | { type: 'partners'; section: LandingPageSection }
      | { type: 'features'; items: LandingPageSection[] };
    const blocks: Block[] = [];
    for (const section of orderedTopLevel) {
      if (isLandingFeatureKey(section.section_key)) {
        const last = blocks[blocks.length - 1];
        if (last?.type === 'features') last.items.push(section);
        else blocks.push({ type: 'features', items: [section] });
        continue;
      }
      if (section.section_key === 'hero') blocks.push({ type: 'hero', section });
      else if (section.section_key === 'cta') blocks.push({ type: 'cta', section });
      else if (section.section_key === 'partners') blocks.push({ type: 'partners', section });
    }
    return blocks;
  }, [orderedTopLevel]);
  const partnerLogoSections = useMemo(
    () =>
      sections
        .filter((s) => isLandingPartnerLogoKey(s.section_key))
        .sort((a, b) => a.section_order - b.section_order),
    [sections]
  );
  const slides = useMemo(() => buildCarouselSlides(sections), [sections]);
  const heroTextBaked = slides.some((s) => s.textBaked);
  const slideCount = slides.length;
  const rootRef = useScrollReveal(!loading && !error);

  useEffect(() => {
    setSlideIndex(0);
  }, [slideCount]);

  const goTo = useCallback(
    (index: number) => {
      if (slideCount <= 0) return;
      setSlideIndex(((index % slideCount) + slideCount) % slideCount);
    },
    [slideCount]
  );

  const goNext = useCallback(() => goTo(slideIndex + 1), [goTo, slideIndex]);
  const goPrev = useCallback(() => goTo(slideIndex - 1), [goTo, slideIndex]);

  useEffect(() => {
    if (paused || slideCount <= 1) return;
    const id = window.setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slideCount);
    }, CAROUSEL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [paused, slideCount]);

  const handleCtaClick = (section: LandingPageSection) => {
    if (isSignUpCta(section)) {
      onSignUpClick();
      return;
    }
    onLoginClick();
  };

  if (loading) {
    return (
      <div className="landing-page landing-page--loading">
        <div className="landing-loading-spinner" aria-label="로딩 중" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page landing-page--error">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="landing-page" ref={rootRef}>
      <LandingNav onLoginClick={onLoginClick} />

      {orderedBlocks.map((block) => {
        if (block.type === 'hero') {
          const hero = block.section;
          return (
            <section
              key={hero.id}
              className={`landing-hero${heroTextBaked ? ' landing-hero--baked' : ''}`}
              id="hero"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
              onFocusCapture={() => setPaused(true)}
              onBlurCapture={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setPaused(false);
                }
              }}
            >
              <div
                className="landing-hero-carousel"
                role="region"
                aria-roledescription="carousel"
                aria-label="메인 이미지"
                onTouchStart={(e) => {
                  touchStartX.current = e.changedTouches[0]?.clientX ?? null;
                }}
                onTouchEnd={(e) => {
                  const start = touchStartX.current;
                  const end = e.changedTouches[0]?.clientX;
                  touchStartX.current = null;
                  if (start == null || end == null) return;
                  const delta = end - start;
                  if (Math.abs(delta) < 48) return;
                  if (delta < 0) goNext();
                  else goPrev();
                }}
              >
                <div
                  className="landing-hero-track"
                  onClick={slideCount > 1 ? goNext : undefined}
                  role={slideCount > 1 ? 'button' : undefined}
                  tabIndex={slideCount > 1 ? 0 : undefined}
                  aria-label={slideCount > 1 ? '다음 이미지' : undefined}
                  onKeyDown={
                    slideCount > 1
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goNext();
                          }
                        }
                      : undefined
                  }
                >
                  {slides.map((slide, i) => (
                    <div
                      key={slide.id}
                      className={`landing-hero-slide${i === slideIndex ? ' is-active' : ''}`}
                      aria-hidden={i !== slideIndex}
                    >
                      {slide.imageUrl ? (
                        <picture>
                          {slide.mobileImageUrl && (
                            <source media={HERO_MOBILE_MEDIA} srcSet={slide.mobileImageUrl} />
                          )}
                          <img
                            src={slide.imageUrl}
                            alt={slide.alt}
                            className="landing-hero-slide-image"
                            loading={i === 0 ? 'eager' : 'lazy'}
                            draggable={false}
                          />
                        </picture>
                      ) : (
                        <div
                          className={`landing-hero-slide-placeholder landing-hero-slide-placeholder--${slide.tone}`}
                          aria-label={slide.alt}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="landing-hero-overlay" aria-hidden />

                {slideCount > 1 && (
                  <div className="landing-hero-dots" role="tablist" aria-label="슬라이드 선택">
                    {slides.map((slide, i) => (
                      <button
                        key={slide.id}
                        type="button"
                        role="tab"
                        aria-selected={i === slideIndex}
                        aria-label={`${i + 1}번째 이미지`}
                        className={`landing-hero-dot${i === slideIndex ? ' is-active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          goTo(i);
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="landing-hero-content" data-landing-reveal>
                <p className="landing-hero-brand">
                  <BrandLogo className="cp-brand-logo--hero" />
                </p>
                <h1 className="landing-hero-title">{hero.subtitle || hero.title}</h1>
                {hero.body && <p className="landing-hero-body">{hero.body}</p>}
                {hero.cta_label && (
                  <button
                    type="button"
                    className="landing-hero-cta-btn"
                    onClick={() => handleCtaClick(hero)}
                  >
                    {hero.cta_label}
                  </button>
                )}
              </div>
            </section>
          );
        }

        if (block.type === 'features') {
          return (
            <div key={block.items.map((s) => s.id).join('-')} className="landing-features">
              {block.items.map((section, index) => {
                const isEven = index % 2 === 0;
                const mediaSlides = buildFeatureMediaSlides(sections, section.section_key);
                return (
                  <section
                    key={section.id}
                    className={`landing-feature${isEven ? ' landing-feature--reverse' : ''}`}
                    id={section.section_key}
                    data-landing-reveal
                    style={{ transitionDelay: `${Math.min(index, 6) * 80}ms` }}
                  >
                    <div className="landing-feature-content">
                      <LandingFeatureMedia slides={mediaSlides} alt={section.title} />
                      <div className="landing-feature-text">
                        <h2 className="landing-feature-title">{section.title}</h2>
                        {section.subtitle ? (
                          <p className="landing-feature-subtitle">{section.subtitle}</p>
                        ) : null}
                        {section.body && <p className="landing-feature-body">{section.body}</p>}
                        {section.cta_label ? (
                          <button
                            type="button"
                            className="landing-feature-cta-btn"
                            onClick={() => handleCtaClick(section)}
                          >
                            {section.cta_label}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          );
        }

        if (block.type === 'cta') {
          const cta = block.section;
          return (
            <section key={cta.id} className="landing-cta" id="cta" data-landing-reveal>
              <div className="landing-cta-content">
                <h2 className="landing-cta-title">{cta.title}</h2>
                {(cta.body || cta.subtitle) && (
                  <p className="landing-cta-subtitle">{cta.body || cta.subtitle}</p>
                )}
                {cta.cta_label && (
                  <button
                    type="button"
                    className="landing-cta-btn"
                    onClick={() => handleCtaClick(cta)}
                  >
                    {cta.cta_label}
                  </button>
                )}
              </div>
            </section>
          );
        }

        const partners = block.section;
        return (
          <section key={partners.id} className="landing-partners" id="partners" data-landing-reveal>
            <div className="landing-partners-inner">
              <h2 className="landing-partners-title">{partners.title || '협업기관'}</h2>
              {(partners.subtitle || partners.id === 'partners-fallback') && (
                <p className="landing-partners-subtitle">
                  {partners.subtitle || '함께하는 기관·학교'}
                </p>
              )}
              <div className="landing-partners-logos" role="list">
                {partnerLogoSections.map((partner) => {
                  const imageUrl = sectionImageUrl(partner);
                  const label = (partner.title || '').trim() || '협업기관';
                  const rawLink = (partner.cta_link || '').trim();
                  const href = rawLink
                    ? /^https?:\/\//i.test(rawLink)
                      ? rawLink
                      : `https://${rawLink.replace(/^\/\//, '')}`
                    : null;
                  const media = imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="landing-partners-logo-img"
                      loading="lazy"
                    />
                  ) : (
                    <span className="landing-partners-logo-placeholder" aria-hidden>
                      {label}
                    </span>
                  );
                  const tip = (
                    <span className="landing-partners-logo-tip" role="tooltip">
                      {label}
                    </span>
                  );
                  return (
                    <div key={partner.id} className="landing-partners-logo-item" role="listitem">
                      {href ? (
                        <a
                          className="landing-partners-logo-link"
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${label} 사이트 열기`}
                        >
                          {media}
                          {tip}
                        </a>
                      ) : (
                        <div
                          className="landing-partners-logo-static"
                          tabIndex={0}
                          aria-label={label}
                        >
                          {media}
                          {tip}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}

      <PlatformCompanyInfo reveal />
    </div>
  );
};

export default LandingPage;
